import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import { selectUser } from "../../store/authSlice";

const DOMAIN = "@navigatio.guru";

const REASON_CATEGORIES = [
  { value: "employee_resignation", label: "Employee resignation" },
  { value: "termination", label: "Termination" },
  { value: "role_change", label: "Role change" },
  { value: "security_incidents", label: "Security incidents" },
  { value: "org_restructure", label: "Org restructure" },
];

const ADMIN_STATUS_LABEL = {
  0: "Pending Invite",
  1: "Active",
  2: "Suspended",
  3: "Revoked",
};

const ROLE_VISIBILITY = {
  1: [1, 2, 3, 4, 5, 6, 7],
  2: [3, 4, 5, 6, 7],
  3: [4, 5, 6, 7],
  4: [5],
  5: [],
  6: [],
  7: [],
};

const SUPER_ADMIN_ROLE_ID = 1;

function getCurrentUser() {
  const keys = ["user", "admin_user", "authUser", "auth", "currentUser", "adminUser", "session"];
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.role_id) return parsed;
      if (parsed?.user?.role_id) return parsed.user;
      if (parsed?.data?.role_id) return parsed.data;
    } catch {
      continue;
    }
  }
  return null;
}

function getRoleFromToken() {
  const keys = ["token", "access_token", "accessToken", "authToken", "admin_token"];
  for (const key of keys) {
    try {
      const token = localStorage.getItem(key);
      if (!token) continue;
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload?.role_id) return payload.role_id;
    } catch {
      continue;
    }
  }
  return null;
}

const emptyForm = {
  first_name: "",
  last_name: "",
  mobile_no: "",
  email: "",
  password: "",
  role_id: "",
  reports_to_user_id: "",
  can_access_org_section: false,
  team_lead_id: "",
  agent_ids: [],
};

function formatReportsTo(u) {
  if (!u?.reports_to) return "—";
  const p = u.reports_to;
  const n = `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return n || p.email || "—";
}

const EyeIcon = ({ show }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    {show ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </>
    )}
  </svg>
);

export default function AdminUsersList() {
  const reduxUser = useSelector(selectUser);
  const [rows, setRows]             = useState([]);
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);
  const [editItem, setEdit]         = useState(null);
  const [confirmId, setConfirmId]   = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [showPassword, setShowPass] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [hi, setHi]                 = useState(null);
  const [hiLoading, setHiLoading]   = useState(false);
  const [selected, setSelected]     = useState(() => new Set());
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonForm, setReasonForm] = useState({ category: "", detail: "", reassign_to_id: "" });
  const [reasonReassignMeta, setReasonReassignMeta] = useState(null);
  const [unsuspendUser, setUnsuspendUser] = useState(null);
  const [reportingModal, setReportingModal] = useState(null);
  const [reportingForm, setReportingForm] = useState({ new_parent_id: "", category: "", detail: "" });
  const [moveMgrModal, setMoveMgrModal] = useState(null);
  const [moveMgrManagers, setMoveMgrManagers] = useState([]);
  const [moveMgrState, setMoveMgrState] = useState({
    new_admin_id: "",
    assignments: [],
    category: "",
    detail: "",
  });
  const [bulkModal, setBulkModal]   = useState(false);
  const [bulkForm, setBulkForm]     = useState({ new_parent_id: "", category: "", detail: "" });
  const [bulkParentCandidates, setBulkParentCandidates] = useState([]);
  const [bulkHiLoading, setBulkHiLoading] = useState(false);
  const [bulkCurrentParentSummary, setBulkCurrentParentSummary] = useState("");
  const selectedRowsRef = useRef(new Map());

  const currentUser    = reduxUser || getCurrentUser();
  const currentRoleId  = Number(currentUser?.role_id ?? getRoleFromToken() ?? 1);
  const currentUserId  = currentUser?.user_id ?? currentUser?.id ?? null;
  const isSuperAdmin   = currentRoleId === 1;
  const isAdmin        = currentRoleId === 2;

  const visibleRoleIds = ROLE_VISIBILITY[currentRoleId] ?? [];
  const filteredRoles  = roles.filter(r => visibleRoleIds.includes(Number(r.role_id)));

  const [pwForm, setPwForm] = useState({
    current_password: "",
    new_password:     "",
    confirm_password: "",
  });
  const [showPw, setShowPw] = useState({
    current: false,
    new:     false,
    confirm: false,
  });

  const [search, setSearch]         = useState("");
  const [reportsToFilter, setReportsToFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage]             = useState(1);
  const [limit, setLimit]           = useState(10);
  const [meta, setMeta]             = useState({ total:0, page:1, limit:10, totalPages:1 });

  const loadRoles = async () => {
    try {
      const res = await api.get("/admin/roles");
      setRoles(res.data?.roles || []);
    } catch {
      toast.error("Failed to load roles");
    }
  };

  const loadUsers = async (opts = {}) => {
    const nextPage   = opts.page    ?? page;
    const nextLimit  = opts.limit   ?? limit;
    const nextSearch = opts.search  !== undefined ? opts.search  : search;
    const nextRole   = opts.role_id !== undefined ? opts.role_id : roleFilter;
    const nextRt =
      opts.reports_to_search !== undefined ? opts.reports_to_search : reportsToFilter;

    setLoading(true);
    try {
      const res = await api.get("/admin/admin-users", {
        params: {
          search:   nextSearch || undefined,
          role_id:  nextRole || undefined,
          role_ids: !nextRole ? visibleRoleIds.join(",") : undefined,
          reports_to_search: String(nextRt || "").trim() || undefined,
          page:     nextPage,
          limit:    nextLimit,
        },
      });

      let users = res.data?.users || [];

      users = users.filter(u => visibleRoleIds.includes(Number(u.role_id)));

      if (!isSuperAdmin && currentUserId) {
        users = users.filter(u =>
          Number(u.user_id ?? u.id) !== Number(currentUserId)
        );
      }
      setRows(users);
      setMeta(res.data?.meta || { total:0, page:nextPage, limit:nextLimit, totalPages:1 });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadHierarchy = useCallback(async (forRole, editUserId) => {
    if (!forRole) {
      setHi(null);
      return;
    }
    setHiLoading(true);
    try {
      const res = await api.get("/admin/admin-users/hierarchy-options", {
        params: { for_role: forRole, edit_user_id: editUserId || undefined },
      });
      setHi(res.data || null);
    } catch {
      toast.error("Failed to load hierarchy options");
      setHi(null);
    } finally {
      setHiLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    loadUsers();
  }, []);

  const reportsToDebounceSkip = useRef(true);
  useEffect(() => {
    if (reportsToDebounceSkip.current) {
      reportsToDebounceSkip.current = false;
      return;
    }
    const h = setTimeout(() => {
      setPage(1);
      loadUsers({ page: 1, reports_to_search: reportsToFilter });
    }, 400);
    return () => clearTimeout(h);
  }, [reportsToFilter]);

  useEffect(() => {
    if (modal !== "create" && modal !== "edit") return;
    const rid = Number(form.role_id);
    if (!rid) return;
    loadHierarchy(rid, editItem?.user_id);
  }, [form.role_id, modal, editItem?.user_id, loadHierarchy]);

  useEffect(() => {
    if (modal !== "create" && modal !== "edit") return;
    const rid = Number(form.role_id);
    if (!rid || rid === SUPER_ADMIN_ROLE_ID) return;
    if (hiLoading) return;
    const candidates = hi?.parent_candidates || [];
    if (!candidates.length) return;
    const firstId = candidates[0]?.user_id;
    if (firstId == null) return;

    setForm((f) => {
      const inList =
        f.reports_to_user_id &&
        candidates.some((p) => String(p.user_id) === String(f.reports_to_user_id));
      if (inList) return f;
      // Auto-assign to first candidate (Super Admin when no direct manager exists for the role)
      return { ...f, reports_to_user_id: String(firstId) };
    });
  }, [hi, hiLoading, form.role_id, modal]);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers({ page:1, search, role_id: roleFilter, reports_to_search: reportsToFilter });
  };

  const onReset = () => {
    setSearch("");
    setReportsToFilter("");
    setRoleFilter("");
    setPage(1);
    loadUsers({ page:1, search:"", role_id:"", reports_to_search: "" });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value);
    setLimit(v);
    setPage(1);
    loadUsers({ page:1, limit:v });
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    loadUsers({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    loadUsers({ page: p });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (name === "email") {
      setEmailError(value && !value.endsWith(DOMAIN) ? `Only ${DOMAIN} emails are allowed` : "");
    }
  };

  const openCreate = () => {
    setEdit(null);
    const next = { ...emptyForm };
    if (isAdmin) next.reports_to_user_id = currentUserId || "";
    setForm(next);
    setEmailError("");
    setShowPass(false);
    setModal("create");
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      first_name: row.first_name,
      last_name: row.last_name,
      mobile_no: row.mobile_no || "",
      email: row.email,
      password: "",
      role_id: String(row.role_id),
      reports_to_user_id: row.reports_to_user_id || "",
      can_access_org_section: !!Number(row.can_access_org_section),
      team_lead_id: "",
      agent_ids: [],
    });
    setEmailError("");
    setModal("edit");
    setTimeout(() => {
      api
        .get("/admin/admin-users/hierarchy-options", {
          params: { for_role: row.role_id, edit_user_id: row.user_id },
        })
        .then((res) => {
          const d = res.data;
          const tls = d?.current_team_leads || [];
          const ag = d?.current_agents || [];
          setForm((f) => ({
            ...f,
            team_lead_id: tls[0]?.user_id || "",
            agent_ids: ag.map((a) => a.user_id),
          }));
        })
        .catch(() => {});
    }, 0);
  };

  const openChangePassword = (row) => {
    setEdit(row);
    setPwForm({ current_password:"", new_password:"", confirm_password:"" });
    setShowPw({ current:false, new:false, confirm:false });
    setModal("password");
  };
  
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.last_name.trim())  { toast.error("Last name is required");  return; }
    if (!form.mobile_no.trim())  { toast.error("Mobile number is required"); return; }
   if (form.mobile_no.length !== 10) { toast.error("Mobile number must be exactly 10 digits"); return; }
    if (!form.email.trim())      { toast.error("Email is required"); return; }
    if (!form.email.endsWith(DOMAIN)) { setEmailError(`Only ${DOMAIN} emails are allowed`); return; }
    if (!form.role_id)           { toast.error("Please select a role"); return; }
    if (modal === "create" && !form.password.trim()) { toast.error("Password is required"); return; }

    const rid = Number(form.role_id);
    if (rid !== 1 && !form.reports_to_user_id) {
      toast.error("Select reporting manager / admin");
      return;
    }

    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        mobile_no: form.mobile_no,
        email: form.email,
        role_id: form.role_id,
        reports_to_user_id: rid === 1 ? null : form.reports_to_user_id || null,
        can_access_org_section: rid === 2 ? !!form.can_access_org_section : undefined,
        team_lead_id: rid === 3 ? form.team_lead_id || null : undefined,
        agent_ids: rid === 3 && form.agent_ids?.length ? form.agent_ids : undefined,
      };
      if (modal === "edit") {
        await api.put(`/admin/admin-users/${editItem.user_id}`, payload);
        toast.success("User updated successfully");
      } else {
        await api.post("/admin/admin-users", { ...payload, password: form.password });
        toast.success("User created — credentials sent to email");
      }
      setModal(false);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.new_password)           { toast.error("New password is required"); return; }
    if (pwForm.new_password.length < 8) { toast.error("Min. 8 characters required"); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error("Passwords do not match"); return; }

    try {
      await api.patch(`/admin/admin-users/${editItem.user_id}/change-password`, {
        current_password: pwForm.current_password,
        new_password:     pwForm.new_password,
      });
      toast.success("Password changed successfully");
      setModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to change password");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/admin-users/${confirmId}`);
      toast.success("User deactivated");
      setConfirmId(null);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  const handleResendInviteLink = async (uid) => {
    try {
      await api.put(`/admin/admin-users/invite/resend-link/${uid}`);
      toast.success("Create Password Link Sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Resend failed");
    }
  };

  const openReasonModal = async (type, user) => {
    setReasonForm({ category: "", detail: "", reassign_to_id: "" });
    setReasonReassignMeta(null);
    setReasonModal({ type, user, phase: "form" });
    try {
      const res = await api.get("/admin/admin-users/hierarchy-options", {
        params: { suspend_reassign_options: "1", for_user_id: user.user_id },
      });
      setReasonReassignMeta(res.data);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to load reassignment options";
      toast.error(msg);
      setReasonReassignMeta({
        direct_report_count: Number(e?.response?.data?.direct_report_count) || 0,
        reassign_required: false,
        reassign_label: null,
        candidates: [],
        loadError: msg,
      });
    }
  };

  const hasDirectReportsToReassign = (meta) => Number(meta?.direct_report_count || 0) > 0;

  const proceedReasonToConfirm = () => {
    if (!reasonForm.category) {
      toast.error("Select a reason category");
      return;
    }
    if (reasonReassignMeta?.loadError) {
      toast.error("Fix reassignment options and try again");
      return;
    }
    if (hasDirectReportsToReassign(reasonReassignMeta)) {
      if (!reasonReassignMeta.candidates?.length) {
        toast.error("No eligible user is available in your scope to receive these reports.");
        return;
      }
      if (!reasonForm.reassign_to_id) {
        toast.error("Choose another user to assign their direct reports to");
        return;
      }
    }
    setReasonModal((m) => (m ? { ...m, phase: "confirm" } : m));
  };

  const submitReasonAction = async () => {
    if (!reasonForm.category || reasonModal?.phase !== "confirm") {
      toast.error("Confirm from the review step");
      return;
    }
    const { type, user } = reasonModal || {};
    const payload = {
      reason_category: reasonForm.category,
      reason_detail: reasonForm.detail,
    };
    if (hasDirectReportsToReassign(reasonReassignMeta) && reasonForm.reassign_to_id) {
      payload.reassign_subordinates_to_user_id = reasonForm.reassign_to_id;
    }
    try {
      if (type === "suspend") {
        await api.patch(`/admin/admin-users/${user.user_id}/suspend`, payload);
        toast.success("User suspended");
      } else {
        await api.patch(`/admin/admin-users/${user.user_id}/revoke`, payload);
        toast.success("Access revoked");
      }
      setReasonModal(null);
      setReasonReassignMeta(null);
      setReasonForm({ category: "", detail: "", reassign_to_id: "" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Action failed");
    }
  };

  const submitUnsuspend = async () => {
    if (!unsuspendUser) return;
    try {
      await api.patch(`/admin/admin-users/${unsuspendUser.user_id}/unsuspend`);
      toast.success("User reactivated");
      setUnsuspendUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to reactivate");
    }
  };

  const submitChangeReporting = async () => {
    if (!reportingModal?.user) return;
    if (!reportingForm.new_parent_id) {
      toast.error("Select new reporting user");
      return;
    }
    if (!reportingForm.category) {
      toast.error("Select reason category");
      return;
    }
    try {
      await api.post(`/admin/admin-users/${reportingModal.user.user_id}/change-reporting`, {
        new_reports_to_user_id: reportingForm.new_parent_id,
        reason_category: reportingForm.category,
        reason_detail: reportingForm.detail,
      });
      toast.success("Reporting updated");
      setReportingModal(null);
      setReportingForm({ new_parent_id: "", category: "", detail: "" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed");
    }
  };

  const submitMoveManager = async () => {
    if (!moveMgrModal?.user) return;
    if (!moveMgrState.new_admin_id || !moveMgrState.category) {
      toast.error("Select target admin and reason");
      return;
    }
    const tls = moveMgrModal.teamLeads || [];
    const assignments = (moveMgrState.assignments || []).filter((a) => a.target_manager_id);
    if (tls.length > 0) {
      const allSet = tls.every((tl) =>
        assignments.some((a) => a.team_lead_id === tl.user_id && a.target_manager_id)
      );
      if (!allSet) {
        toast.error("Choose a target manager for every team lead");
        return;
      }
    }
    try {
      await api.post("/admin/admin-users/move-manager", {
        manager_id: moveMgrModal.user.user_id,
        new_admin_id: moveMgrState.new_admin_id,
        team_lead_assignments: assignments,
        reason_category: moveMgrState.category,
        reason_detail: moveMgrState.detail,
      });
      toast.success("Manager moved");
      setMoveMgrModal(null);
      setMoveMgrState({ new_admin_id: "", assignments: [], category: "", detail: "" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Move failed");
    }
  };

  const submitBulkReassign = async () => {
    if (!bulkForm.new_parent_id || !bulkForm.category) {
      toast.error("Select new parent and reason");
      return;
    }
    const ids = [...selected];
    if (!ids.length) {
      toast.error("Select users");
      return;
    }
    try {
      await api.post(
        "/admin/admin-users/bulk-change-reporting",
        {
          user_ids: ids,
          new_reports_to_user_id: bulkForm.new_parent_id,
          reason_category: bulkForm.category,
          reason_detail: bulkForm.detail,
        },
        // Bounded by however many users are selected in the UI (an admin's
        // explicit selection, not the whole org) — lighter than the other two
        // flagged operations, so a smaller override. 60s, scoped to this call only.
        { timeout: 60000 }
      );
      toast.success("Users reassigned");
      setBulkModal(false);
      setBulkForm({ new_parent_id: "", category: "", detail: "" });
      setBulkParentCandidates([]);
      setBulkCurrentParentSummary("");
      selectedRowsRef.current.clear();
      setSelected(new Set());
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Bulk reassign failed");
    }
  };

  const openBulkReassignModal = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one user");
      return;
    }
    const picked = [...selected]
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (picked.length !== selected.size) {
      toast.error("Reload the list and try again");
      return;
    }
    const roleIds = new Set(picked.map((p) => Number(p.role_id)));
    if (roleIds.size > 1) {
      toast.error("Select users with the same role");
      return;
    }
    const rid = [...roleIds][0];
    if (rid === SUPER_ADMIN_ROLE_ID) {
      toast.error("Cannot reassign Super Admin reporting in bulk");
      return;
    }

    const rtIds = picked.map((p) => p.reports_to_user_id || "").filter(Boolean);
    const uniqRt = new Set(rtIds.map(String));
    if (uniqRt.size === 0) {
      setBulkCurrentParentSummary("No supervisor assigned yet for selected user(s).");
    } else if (uniqRt.size === 1) {
      const row = picked.find((p) => p.reports_to_user_id);
      const rn = formatReportsTo(row);
      const rrole = row?.reports_to?.role?.name || "—";
      if (Number(rid) === 5) {
        setBulkCurrentParentSummary(`Current team lead / supervisor: ${rn} (${rrole})`);
      } else {
        setBulkCurrentParentSummary(`Current reports to: ${rn} (${rrole})`);
      }
    } else {
      setBulkCurrentParentSummary(
        "Selected users report to different people — choose one new supervisor for all."
      );
    }

    setBulkModal(true);
    setBulkHiLoading(true);
    setBulkForm({ new_parent_id: "", category: "", detail: "" });
    setBulkParentCandidates([]);
    try {
      const res = await api.get("/admin/admin-users/hierarchy-options", { params: { for_role: rid } });
      const cands = res.data?.parent_candidates || [];
      setBulkParentCandidates(cands);
      const first = cands[0];
      setBulkForm((f) => ({
        ...f,
        new_parent_id: first?.user_id != null ? String(first.user_id) : "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load parent options");
      setBulkModal(false);
      setBulkCurrentParentSummary("");
    } finally {
      setBulkHiLoading(false);
    }
  };

  const openReporting = async (row) => {
    const rid = Number(row.role_id);
    const parentRole =
      rid === 5 ? 4 : rid === 4 ? 3 : rid === 3 ? 2 : rid === 2 ? 1 : null;
    if (!parentRole) {
      toast.error("Cannot change reporting for this role here");
      return;
    }
    try {
      const res = await api.get("/admin/admin-users/hierarchy-options", { params: { for_role: rid } });
      setReportingModal({ user: row, parents: res.data?.parent_candidates || [] });
      setReportingForm({
        new_parent_id: row.reports_to_user_id || "",
        category: "",
        detail: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load options");
    }
  };

  const openMoveManagerFlow = async (row) => {
    if (Number(row.role_id) !== 3) return;
    try {
      const [tlsRes, adminsRes] = await Promise.all([
        api.get("/admin/admin-users/hierarchy-options", {
          params: { team_leads_under_manager: row.user_id },
        }),
        api.get("/admin/admin-users/hierarchy-options", { params: { list_admins: "1" } }),
      ]);
      const tls = tlsRes.data?.team_leads || [];
      const admins = adminsRes.data?.admin_users || [];
      setMoveMgrModal({ user: row, admins, teamLeads: tls });
      setMoveMgrManagers([]);
      setMoveMgrState({
        new_admin_id: "",
        assignments: tls.map((t) => ({ team_lead_id: t.user_id, target_manager_id: "" })),
        category: "",
        detail: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load move data");
    }
  };

  const toggleRow = (id) => {
    const row = rows.find((r) => r.user_id === id);
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
        selectedRowsRef.current.delete(id);
      } else {
        n.add(id);
        if (row) selectedRowsRef.current.set(id, row);
      }
      return n;
    });
  };

  const selectableRows = rows.filter((r) => Number(r.role_id) !== SUPER_ADMIN_ROLE_ID);

  const toggleAllPage = () => {
    if (selectableRows.length && selectableRows.every((r) => selected.has(r.user_id))) {
      setSelected(new Set());
      selectedRowsRef.current.clear();
      return;
    }
    const next = new Set(selectableRows.map((r) => r.user_id));
    setSelected(next);
    selectedRowsRef.current.clear();
    for (const r of selectableRows) selectedRowsRef.current.set(r.user_id, r);
  };

  const canOrgMove = isSuperAdmin || isAdmin || currentRoleId === 3 || currentRoleId === 4;

  const roleNameLooksLikeTeamLead = (row) => {
    const n = (row.role?.name || "").toLowerCase();
    return (n.includes("team") && n.includes("lead")) || n.includes("teamlead");
  };
  const roleNameLooksLikeAgent = (row) => {
    const n = (row.role?.name || "").toLowerCase();
    return n.includes("agent");
  };

  const canRevokeUi = (row) => {
    if (Number(row.role_id) === SUPER_ADMIN_ROLE_ID) return false;
    if (Number(row.user_id) === Number(currentUserId)) return false;
    if (isSuperAdmin) return true;
    if (isAdmin && ![1, 2].includes(Number(row.role_id))) return true;
    if (currentRoleId === 3) {
      if ([4, 5].includes(Number(row.role_id))) return true;
      if (roleNameLooksLikeTeamLead(row) || roleNameLooksLikeAgent(row)) return true;
    }
    if (currentRoleId === 4) {
      if (Number(row.role_id) === 5) return true;
      if (roleNameLooksLikeAgent(row)) return true;
    }
    return false;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Organisation Users</h1>
          <p className="text-sm text-gray-600 mt-1">Manage Organisation users and their roles</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canOrgMove && (
            <button
              type="button"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={openBulkReassignModal}
            >
              Bulk reassign ({selected.size})
            </button>
          )}
          <button
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
            onClick={openCreate}
          >
            + Add User
          </button>
        </div>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:min-w-[240px] sm:flex-1">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
            placeholder="Name, email or mobile..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:min-w-[180px] sm:w-auto">
          <label className="text-sm font-semibold">Role</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
            value={roleFilter}
            onChange={(e) => {
              const v = e.target.value;
              setRoleFilter(v);
              setPage(1);
              loadUsers({
                page: 1,
                role_id: v,
                search,
                reports_to_search: reportsToFilter,
              });
            }}
          >
            <option value="">All Roles</option>
            {filteredRoles.map(r => (
              <option key={r.role_id} value={r.role_id}>{r.name}</option>
            ))}
          </select>
        </div>

        <div className="w-full sm:min-w-[220px] sm:w-auto">
          <label className="text-sm font-semibold">Reports to</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
            placeholder="Type supervisor name or email…"
            value={reportsToFilter}
            onChange={(e) => setReportsToFilter(e.target.value)}
          />
        </div>

        <div className="w-full sm:min-w-[140px] sm:w-auto">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button type="submit"
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
            Apply
          </button>
          <button type="button" onClick={onReset}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
            Reset
          </button>
        </div>
      </form>

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Organisation Users List</span>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={goPrev} disabled={meta.page <= 1 || loading} type="button"
              className="rounded-lg border px-3 py-1 text-xs font-semibold disabled:opacity-50">Prev</button>
            <span className="text-xs text-gray-600">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> &nbsp;(Total: <b>{meta.total}</b>)
            </span>
            <button onClick={goNext} disabled={meta.page >= meta.totalPages || loading} type="button"
              className="rounded-lg border px-3 py-1 text-xs font-semibold disabled:opacity-50">Next</button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No Organisation users found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                {canOrgMove && (
                  <th className="px-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={
                        selectableRows.length > 0 &&
                        selectableRows.every((r) => selected.has(r.user_id))
                      }
                      onChange={toggleAllPage}
                      className="rounded border-gray-300"
                    />
                  </th>
                )}
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Reports to</th>
                <th className="px-4 py-3">User Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((u, idx) => (
                <tr key={u.user_id} className="border-t hover:bg-gray-50">
                  {canOrgMove && (
                    <td className="px-2 py-3">
                      <input
                        type="checkbox"
                        disabled={Number(u.role_id) === SUPER_ADMIN_ROLE_ID}
                        title={
                          Number(u.role_id) === SUPER_ADMIN_ROLE_ID
                            ? "Super Admin cannot be bulk-reassigned"
                            : undefined
                        }
                        checked={selected.has(u.user_id)}
                        onChange={() => toggleRow(u.user_id)}
                        className="rounded border-gray-300 disabled:opacity-40"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-semibold">{u.first_name} {u.last_name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.mobile_no || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700">
                      {/* {u.role?.name  || "—"} */}
                      {u.role?.name == "Team Leaders" ? "TL" : u.role?.name  || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700 max-w-[140px] truncate" title={formatReportsTo(u)}>
                    {formatReportsTo(u)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-xs">
                    {ADMIN_STATUS_LABEL[u.status] ?? `Status ${u.status}`}
                  </td>
                  <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                     <button type="button" className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => handleResendInviteLink(u.user_id)}>Resend</button>
                      <button type="button"
                        className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(u)}>Edit</button>
                      <button type="button"
                        className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openChangePassword(u)}>Password</button>
                      {canOrgMove && Number(u.role_id) !== 1 && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => openReporting(u)}>Change reporting</button>
                      )}
                      {isSuperAdmin && Number(u.role_id) === 3 && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-blue-50 text-blue-800"
                          onClick={() => openMoveManagerFlow(u)}>Move mgr</button>
                      )}
                      {canRevokeUi(u) && Number(u.status) === 1 && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-amber-50 text-amber-800"
                          onClick={() => openReasonModal("suspend", u)}>Suspend</button>
                      )}
                      {canRevokeUi(u) && Number(u.status) === 2 && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-emerald-50 text-emerald-800"
                          onClick={() => setUnsuspendUser(u)}>Unsuspend</button>
                      )}
                      {canRevokeUi(u) && [1, 2].includes(Number(u.status)) && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-red-50 text-red-700"
                          onClick={() => openReasonModal("revoke", u)}>Revoke</button>
                      )}
                      {canRevokeUi(u) && (
                        <button type="button"
                          className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-red-50 text-red-600 border-red-200"
                          onClick={() => setConfirmId(u.user_id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {(modal === "create" || modal === "edit") && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-bold text-lg">{modal === "edit" ? "Edit Organisation User" : "Add Organisation User"}</h2>
              <button type="button" className="text-gray-400 hover:text-gray-600 text-xl leading-none" onClick={() => setModal(false)}>×</button>
            </div>
            <form onSubmit={onSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold">First Name</label>
                  <input name="first_name" value={form.first_name} onChange={onChange} placeholder="John"
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm" />
                </div>
                <div>
                  <label className="text-sm font-semibold">Last Name</label>
                  <input name="last_name" value={form.last_name} onChange={onChange} placeholder="Doe"
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm" />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold">Mobile Number</label>
                {/* <input name="mobile_no" value={form.mobile_no} onChange={onChange} placeholder="9876543210"
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm" /> */}
                <input name="mobile_no" value={form.mobile_no} onChange={(e) => {  const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) { setForm(p => ({ ...p, mobile_no: value })); } }} 
                    placeholder="9876543210" maxLength={10} inputMode="numeric"
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">
                  Email <span className="text-gray-400 font-normal text-xs">(must be @navigatio.guru)</span>
                </label>
                <input name="email" value={form.email} onChange={onChange} placeholder="john@navigatio.guru"
                  className={`mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 text-sm ${
                    emailError ? "border-red-400 focus:ring-red-400" : "focus:ring-black"}`} />
                {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
              </div>

              {modal === "create" && (
                <div>
                  <label className="text-sm font-semibold">Password</label>
                  <div className="relative mt-2">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password" value={form.password} onChange={onChange}
                      placeholder="Min. 8 characters"
                      className="w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black text-sm"
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Login credentials will be emailed to the user.</p>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold">Role</label>
                <select name="role_id" value={String(form.role_id)} onChange={onChange}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm">
                  <option value="">— Select a role —</option>
                  {filteredRoles.map(r => (
                    <option key={r.role_id} value={String(r.role_id)}>{r.name}</option>
                  ))}
                </select>
                {filteredRoles.length === 0 && (
                  <p className="mt-1 text-xs text-red-500">No roles available for your permission level</p>
                )}
              </div>

              {Number(form.role_id) > 0 && Number(form.role_id) !== 1 && (
                <div>
                  <label className="text-sm font-semibold">Reports to</label>
                  <select
                    value={form.reports_to_user_id}
                    onChange={(e) => setForm((p) => ({ ...p, reports_to_user_id: e.target.value }))}
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                  >
                    <option value="">— Select —</option>
                    {(hi?.parent_candidates || []).map((p) => (
                      <option key={p.user_id} value={p.user_id}>
                        {p.first_name} {p.last_name} ({p.role?.name || "—"})
                      </option>
                    ))}
                  </select>
                  {hiLoading && (
                    <p className="mt-1 text-xs text-gray-500">Loading options…</p>
                  )}
                  {!hiLoading && hi?.super_admin_fallback && (
                    <p className="mt-1 text-xs text-amber-600">
                      No direct manager found for this role — auto-assigned to Super Admin.
                    </p>
                  )}
                </div>
              )}

              {Number(form.role_id) === 2 && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.can_access_org_section}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, can_access_org_section: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span>Allow access to Menus, Roles &amp; Permissions (Organisation Section)</span>
                </label>
              )}

              {Number(form.role_id) === 3 && (
                <div className="space-y-3 border-t pt-3">
                  <div>
                    <label className="text-sm font-semibold">Team lead (unassigned or current)</label>
                    <select
                      value={form.team_lead_id}
                      onChange={(e) => setForm((p) => ({ ...p, team_lead_id: e.target.value }))}
                      className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
                    >
                      <option value="">— Optional —</option>
                      {Array.from(
                        new Map(
                          [
                            ...(hi?.current_team_leads || []),
                            ...(hi?.unassigned_team_leads || []),
                          ].map((x) => [x.user_id, x])
                        ).values()
                      ).map((p) => (
                        <option key={p.user_id} value={p.user_id}>
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold">Agents (multi-select, unassigned or current)</label>
                    <select
                      multiple
                      size={5}
                      value={form.agent_ids}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          agent_ids: [...e.target.selectedOptions].map((o) => o.value),
                        }))
                      }
                      className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
                    >
                      {Array.from(
                        new Map(
                          [
                            ...(hi?.current_agents || []),
                            ...(hi?.unassigned_agents || []),
                          ].map((x) => [x.user_id, x])
                        ).values()
                      ).map((p) => (
                        <option key={p.user_id} value={p.user_id}>
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Hold Cmd/Ctrl to select multiple.</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit"
                  className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
                  {modal === "edit" ? "Update User" : "Send Invitation"}
                </button>
                <button type="button" onClick={() => setModal(false)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal === "password" && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="font-bold text-lg">Change Password</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {editItem?.first_name} {editItem?.last_name} — {editItem?.email}
                </p>
              </div>
              <button type="button" className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                onClick={() => setModal(false)}>×</button>
            </div>

            <form onSubmit={onChangePassword} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-semibold">New Password</label>
                <div className="relative mt-2">
                  <input
                    type={showPw.new ? "text" : "password"}
                    value={pwForm.new_password}
                    onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                    placeholder="Min. 8 characters"
                    className="w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black text-sm"
                  />
                  <button type="button"
                    onClick={() => setShowPw(p => ({ ...p, new: !p.new }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <EyeIcon show={showPw.new} />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold">Confirm Password</label>
                <div className="relative mt-2">
                  <input
                    type={showPw.confirm ? "text" : "password"}
                    value={pwForm.confirm_password}
                    onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                    placeholder="Re-enter new password"
                    className={`w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 text-sm ${
                      pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password
                        ? "border-red-400 focus:ring-red-400"
                        : "focus:ring-black"
                    }`}
                  />
                  <button type="button"
                    onClick={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <EyeIcon show={showPw.confirm} />
                  </button>
                </div>
                {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
                  <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit"
                  className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
                  Change Password
                </button>
                <button type="button" onClick={() => setModal(false)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reasonModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            {reasonModal.phase === "form" ? (
              <>
                <h3 className="font-bold text-lg">
                  {reasonModal.type === "suspend" ? "Suspend user" : "Revoke access"}
                </h3>
                <p className="text-sm text-gray-600">
                  {reasonModal.user?.first_name} {reasonModal.user?.last_name}
                  {hasDirectReportsToReassign(reasonReassignMeta) && !reasonReassignMeta?.loadError && (
                    <span className="block mt-1 text-amber-800">
                      {reasonReassignMeta.direct_report_count} direct report
                      {reasonReassignMeta.direct_report_count !== 1 ? "s" : ""} must be reassigned first.
                    </span>
                  )}
                </p>
                {reasonReassignMeta?.loadError && (
                  <p className="text-sm text-red-600 border border-red-200 rounded-xl px-3 py-2 bg-red-50">
                    {reasonReassignMeta.loadError}
                  </p>
                )}
                {hasDirectReportsToReassign(reasonReassignMeta) && !reasonReassignMeta?.loadError && (
                  <div>
                    <label className="text-sm font-semibold">
                      {reasonReassignMeta.reassign_label || "Choose another user for their direct reports"}
                    </label>
                    <select
                      className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                      value={reasonForm.reassign_to_id}
                      onChange={(e) => setReasonForm((p) => ({ ...p, reassign_to_id: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      {(reasonReassignMeta.candidates || []).map((c) => (
                        <option key={c.user_id} value={c.user_id}>
                          {c.first_name} {c.last_name} ({c.role?.name})
                        </option>
                      ))}
                    </select>
                    {!reasonReassignMeta.candidates?.length && (
                      <p className="mt-1 text-xs text-red-600">
                        No eligible user in your scope. Ask a Super Admin or add a manager/team lead first.
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-sm font-semibold">Reason category</label>
                  <select
                    className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                    value={reasonForm.category}
                    onChange={(e) => setReasonForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    <option value="">— Select —</option>
                    {REASON_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold">Details (optional)</label>
                  <textarea
                    className="mt-1 w-full border rounded-xl px-3 py-2 text-sm min-h-[80px]"
                    value={reasonForm.detail}
                    onChange={(e) => setReasonForm((p) => ({ ...p, detail: e.target.value }))}
                  />
                </div>
                <div className="flex gap-2 justify-end flex-wrap">
                  <button
                    type="button"
                    className="rounded-xl border px-4 py-2 text-sm"
                    onClick={() => {
                      setReasonModal(null);
                      setReasonReassignMeta(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-xl bg-black px-4 py-2 text-sm text-white"
                    onClick={proceedReasonToConfirm}
                  >
                    Review &amp; confirm
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-bold text-lg">Confirm {reasonModal.type === "suspend" ? "suspend" : "revoke"}</h3>
                <div className="text-sm text-gray-700 space-y-2 border rounded-xl p-3 bg-gray-50">
                  <p>
                    <span className="text-gray-500">User:</span>{" "}
                    <span className="font-medium">
                      {reasonModal.user?.first_name} {reasonModal.user?.last_name}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Action:</span>{" "}
                    {reasonModal.type === "suspend" ? "Suspend (login blocked)" : "Revoke access permanently"}
                  </p>
                  <p>
                    <span className="text-gray-500">Reason:</span>{" "}
                    {REASON_CATEGORIES.find((c) => c.value === reasonForm.category)?.label || reasonForm.category}
                  </p>
                  {reasonForm.detail?.trim() && (
                    <p>
                      <span className="text-gray-500">Details:</span> {reasonForm.detail}
                    </p>
                  )}
                  {hasDirectReportsToReassign(reasonReassignMeta) && reasonForm.reassign_to_id && (
                    <p>
                      <span className="text-gray-500">Reports move to:</span>{" "}
                      {(() => {
                        const c = (reasonReassignMeta.candidates || []).find(
                          (x) => String(x.user_id) === String(reasonForm.reassign_to_id)
                        );
                        return c ? `${c.first_name} ${c.last_name} (${c.role?.name})` : reasonForm.reassign_to_id;
                      })()}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500">This is the final step. Confirm only if everything looks correct.</p>
                <div className="flex gap-2 justify-end flex-wrap">
                  <button
                    type="button"
                    className="rounded-xl border px-4 py-2 text-sm"
                    onClick={() => setReasonModal((m) => (m ? { ...m, phase: "form" } : m))}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="rounded-xl bg-black px-4 py-2 text-sm text-white"
                    onClick={submitReasonAction}
                  >
                    {reasonModal.type === "suspend" ? "Confirm suspend" : "Confirm revoke"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {unsuspendUser && (
        <ConfirmDialog
          message={`Reactivate ${unsuspendUser.first_name} ${unsuspendUser.last_name}? They can sign in again after this (new session).`}
          confirmLabel="Unsuspend"
          confirmClassName="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
          onConfirm={submitUnsuspend}
          onCancel={() => setUnsuspendUser(null)}
        />
      )}

      {reportingModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg">Change reporting</h3>
            <p className="text-xs text-gray-600">
              Current: {formatReportsTo(reportingModal.user)} → select new reporting user.
            </p>
            <select
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={reportingForm.new_parent_id}
              onChange={(e) => setReportingForm((p) => ({ ...p, new_parent_id: e.target.value }))}
            >
              <option value="">— Select —</option>
              {(reportingModal.parents || []).map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.first_name} {p.last_name} ({p.role?.name})
                </option>
              ))}
            </select>
            <select
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={reportingForm.category}
              onChange={(e) => setReportingForm((p) => ({ ...p, category: e.target.value }))}
            >
              <option value="">Reason category —</option>
              {REASON_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <textarea
              placeholder="Details (optional)"
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={reportingForm.detail}
              onChange={(e) => setReportingForm((p) => ({ ...p, detail: e.target.value }))}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm" onClick={() => setReportingModal(null)}>
                Cancel
              </button>
              <button type="button" className="rounded-xl bg-black px-4 py-2 text-sm text-white" onClick={submitChangeReporting}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {moveMgrModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg">Move manager to another admin</h3>
            <p className="text-sm text-gray-600">
              Reassign each team lead to a manager under the target admin, then move the manager.
            </p>
            <div>
              <label className="text-sm font-semibold">Target admin</label>
              <select
                className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                value={moveMgrState.new_admin_id}
                onChange={async (e) => {
                  const v = e.target.value;
                  setMoveMgrState((s) => ({
                    ...s,
                    new_admin_id: v,
                    assignments: (s.assignments || []).map((a) => ({ ...a, target_manager_id: "" })),
                  }));
                  if (v) {
                    try {
                      const res = await api.get("/admin/admin-users/hierarchy-options", {
                        params: { managers_under_admin: v },
                      });
                      setMoveMgrManagers(res.data?.managers_under_admin || []);
                    } catch {
                      setMoveMgrManagers([]);
                    }
                  } else setMoveMgrManagers([]);
                }}
              >
                <option value="">— Select admin —</option>
                {(moveMgrModal.admins || []).map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {a.first_name} {a.last_name}
                  </option>
                ))}
              </select>
            </div>
            {(moveMgrModal.teamLeads || []).length === 0 && (
              <p className="text-sm text-gray-500">No team leads under this manager — you can still move the manager line.</p>
            )}
            {(moveMgrModal.teamLeads || []).map((tl) => {
              const asg = moveMgrState.assignments.find((x) => x.team_lead_id === tl.user_id);
              return (
                <div key={tl.user_id} className="flex flex-col gap-1 border rounded-lg p-2">
                  <span className="text-xs font-semibold">
                    TL: {tl.first_name} {tl.last_name}
                  </span>
                  <select
                    className="border rounded-lg px-2 py-1 text-sm"
                    value={asg?.target_manager_id || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMoveMgrState((s) => ({
                        ...s,
                        assignments: s.assignments.map((x) =>
                          x.team_lead_id === tl.user_id ? { ...x, target_manager_id: v } : x
                        ),
                      }));
                    }}
                    disabled={!moveMgrState.new_admin_id}
                  >
                    <option value="">— Target manager —</option>
                    {moveMgrManagers.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.first_name} {m.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
            <select
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={moveMgrState.category}
              onChange={(e) => setMoveMgrState((s) => ({ ...s, category: e.target.value }))}
            >
              <option value="">Reason category —</option>
              {REASON_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <textarea
              placeholder="Details (optional)"
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={moveMgrState.detail}
              onChange={(e) => setMoveMgrState((s) => ({ ...s, detail: e.target.value }))}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm" onClick={() => setMoveMgrModal(null)}>
                Cancel
              </button>
              <button type="button" className="rounded-xl bg-black px-4 py-2 text-sm text-white" onClick={submitMoveManager}>
                Apply move
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg">Bulk change reporting</h3>
            <p className="text-sm text-gray-600">{selected.size} users selected.</p>
            {/* <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2">
              Selected users must share the same role. New supervisor options follow hierarchy (e.g. team lead →
              manager → admin → super admin).
            </p> */}
            <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800">
              <span className="font-semibold text-gray-600">Current: </span>
              {bulkCurrentParentSummary || "—"}
            </div>
            <div>
              <label className="text-sm font-semibold">New reports to</label>
              <select
                className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
                value={bulkForm.new_parent_id}
                onChange={(e) => setBulkForm((p) => ({ ...p, new_parent_id: e.target.value }))}
                disabled={bulkHiLoading || bulkParentCandidates.length === 0}
              >
                <option value="">— Select —</option>
                {bulkParentCandidates.map((p) => (
                  <option key={p.user_id} value={p.user_id}>
                    {p.first_name} {p.last_name} ({p.role?.name || "—"})
                  </option>
                ))}
              </select>
              {bulkHiLoading && <p className="mt-1 text-xs text-gray-500">Loading supervisors…</p>}
              {!bulkHiLoading && bulkParentCandidates.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">No eligible supervisors found for your account.</p>
              )}
            </div>
            <select
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={bulkForm.category}
              onChange={(e) => setBulkForm((p) => ({ ...p, category: e.target.value }))}
            >
              <option value="">Reason category —</option>
              {REASON_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <textarea
              placeholder="Details (optional)"
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={bulkForm.detail}
              onChange={(e) => setBulkForm((p) => ({ ...p, detail: e.target.value }))}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="rounded-xl border px-4 py-2 text-sm"
                onClick={() => {
                  setBulkModal(false);
                  setBulkParentCandidates([]);
                  setBulkCurrentParentSummary("");
                }}
              >
                Cancel
              </button>
              <button type="button" className="rounded-xl bg-black px-4 py-2 text-sm text-white" onClick={submitBulkReassign}>
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}



      {confirmId && (
        <ConfirmDialog
          message="This will suspend the user (mapped from legacy delete). Continue?"
          onConfirm={remove}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
}